import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { useApp } from '../../contexts/AppContext';
import { ArrowLeft, Send, Pin, Lock, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';

export default function ForumThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.getForumThreadDetail(id)); }
    catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const submitReply = async () => {
    if (reply.trim().length < 5) return toast.error('Message trop court');
    setPosting(true);
    try {
      await api.createForumPost(id, reply);
      setReply('');
      toast.success('Réponse publiée');
      load();
    } catch (e) { toast.error(e.detail || 'Erreur'); }
    finally { setPosting(false); }
  };

  const moderate = async (action) => {
    const update = {};
    if (action === 'pin') update.pinned = !data.thread.pinned;
    if (action === 'lock') update.locked = !data.thread.locked;
    if (action === 'delete') {
      if (!window.confirm('Supprimer cette discussion ?')) return;
      update.deleted = true;
    }
    try {
      await api.moderateThread(id, update);
      toast.success('Modération appliquée');
      if (action === 'delete') return navigate(`/reseau/forum/${data.thread.category_slug}`);
      load();
    } catch (e) { toast.error(e.detail || 'Erreur'); }
  };

  const fmt = (s) => s ? new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

  if (loading) return <div className="mc-card p-10 text-center text-muted-foreground">Chargement…</div>;
  if (!data) return <div className="mc-card p-10 text-center text-muted-foreground">Discussion introuvable</div>;
  const { thread, posts } = data;

  return (
    <div className="space-y-4" data-testid="forum-thread">
      <Button onClick={() => navigate(`/reseau/forum/${thread.category_slug}`)} variant="ghost" size="sm" className="-ml-2">
        <ArrowLeft className="w-4 h-4 mr-1" /> Retour à la catégorie
      </Button>

      <div className="mc-card p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {thread.pinned && <Pin className="w-4 h-4 text-[hsl(var(--primary))]" />}
              {thread.locked && <Lock className="w-4 h-4 text-muted-foreground" />}
              <h1 className="text-xl sm:text-2xl font-black">{thread.title}</h1>
            </div>
            <div className="text-xs text-muted-foreground">par <strong>{thread.author_name}</strong> · {fmt(thread.created_at)}</div>
          </div>
          {isAdmin && (
            <div className="flex gap-1 flex-wrap">
              <Button onClick={() => moderate('pin')} variant="outline" size="sm" className="h-8" data-testid="mod-pin">
                <Pin className="w-3.5 h-3.5 mr-1" /> {thread.pinned ? 'Désépingler' : 'Épingler'}
              </Button>
              <Button onClick={() => moderate('lock')} variant="outline" size="sm" className="h-8" data-testid="mod-lock">
                <Lock className="w-3.5 h-3.5 mr-1" /> {thread.locked ? 'Déverrouiller' : 'Verrouiller'}
              </Button>
              <Button onClick={() => moderate('delete')} variant="outline" size="sm" className="h-8 text-[#b42318] border-[#fecdd3]" data-testid="mod-delete">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Supprimer
              </Button>
            </div>
          )}
        </div>
        <p className="text-sm whitespace-pre-wrap mt-4 text-foreground/85">{thread.body}</p>
      </div>

      {posts.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-2">{posts.length} réponse(s)</div>
          {posts.map(p => (
            <div key={p.id} className="mc-card p-4" data-testid={`forum-post-${p.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0 font-black text-sm">
                  {(p.author_name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm">{p.author_name}</span>
                    {p.author_role === 'admin' && <Shield className="w-3 h-3 text-[hsl(var(--primary))]" />}
                    <span className="text-[10px] text-muted-foreground">{fmt(p.created_at)}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap mt-1 text-foreground/85">{p.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!thread.locked && (
        <div className="mc-card p-4 space-y-2" data-testid="reply-form">
          <textarea
            placeholder="Votre réponse…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={4}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            data-testid="reply-input"
          />
          <div className="flex justify-end">
            <Button onClick={submitReply} disabled={posting} className="mc-btn-primary" size="sm" data-testid="reply-submit">
              <Send className="w-3.5 h-3.5 mr-1.5" /> {posting ? 'Envoi…' : 'Répondre'}
            </Button>
          </div>
        </div>
      )}
      {thread.locked && (
        <div className="mc-card p-4 text-center text-sm text-muted-foreground">
          <Lock className="w-4 h-4 inline mr-1" /> Discussion verrouillée par l'admin
        </div>
      )}
    </div>
  );
}
